import React, { createContext, useContext, useEffect, useState, useCallback } from "react"

import ConsentBanner from "./consentbanner"
import Negosyo from "../../../lib/negosyo"
import deviceTools from "../../../lib/deviceTools";
import { getUserId, pushConsentUpdate, pushConsentWrite, applyGoogleConsent } from "../../../lib/consentHelper";

import { attachTCF } from "./iabtcfgen"
import consentConfig from "../../../config/consent-config";
const ISCMPREGISTERED = consentConfig.IABCMPID > 0;

const ConsentContext = createContext({
	consent: null,
	clientRegion: {region: "", type: ""},
	saveConsent: () => {},
	openConsentBanner: () => {},
	closeConsentBanner: () => {},
	checkConsent: () => {},
})

const ConsentProvider = ({children}) => {
	const [userId] = useState(() => getUserId());
	const [consent, setConsent] = useState(null);
	const [loaded, setLoaded] = useState(false);
	const [showBanner, setShowBanner] = useState(false); // Start false to avoid flash
	const [clientRegion, setClientRegion] = useState({region: "", type: ""});

	// For Cookie Settings
	const [forceShow, setForceShow] = useState(false);
	const [bannerMode, setBannerMode] = useState("settings");

	useEffect(() => {
		let isMounted = true;
		(async () => {
			try {
				const regionData = await Negosyo.getClientRegion();
				setClientRegion(regionData);

				const res = await Negosyo.fetchConsent(userId, "cookie", ISCMPREGISTERED);
				if (isMounted) {
					if (res?.consent) {
						const enriched = attachTCF(res.consent)

						setConsent(enriched);
						setShowBanner(false);

						// Apply consent immediately on load
						applyGoogleConsent(enriched)
						pushConsentUpdate(userId, enriched)
					} else {
						if (!deviceTools.isMobileDevice()) {
							setShowBanner(true); // No consent found, show banner
						}
					}
				}
			} catch (e) {
				//console.log(e);
				if (isMounted && !deviceTools.isMobileDevice()) setShowBanner(true);
			}
			if (isMounted) setLoaded(true);
		})()
		return () => { isMounted = false; };
	}, [userId]);

	const saveConsent = useCallback(async (nextConsent) => {
		const enriched = attachTCF(nextConsent);
		const mode = consent ? "PUT" : "POST";


		// Update External APIs
		applyGoogleConsent(enriched); // Tell Google
		pushConsentUpdate(userId, enriched); // Tell GTM DataLayer

		// Update React State (UI closes)
		setConsent(enriched);
		setShowBanner(false);
		setForceShow(false);

		// Persist to Database
		try {
			await Negosyo.writeConsent(userId, enriched, mode, "cookie", ISCMPREGISTERED);
			pushConsentWrite(userId, enriched, mode);
		} catch (err) {
			console.error("Failed to save consent to API", err);
		}
	}, [userId, consent]);

	const openConsentBanner = (bannerMode) => {
		if (forceShow === false && !(showBanner && loaded)) {
			setForceShow(true);
			setBannerMode(bannerMode);
		}
	}

	const closeConsentBanner = () => {
		setShowBanner(false);
		setForceShow(false);
	};

	const checkConsent = (key, value) => consent === null ? false : consent[key] === value;

	return (
		<ConsentContext.Provider
			value={{
				consent,
				clientRegion,
				saveConsent,
				openConsentBanner,
				closeConsentBanner,
				checkConsent,
			}}
		>
			{children}
			{((showBanner && loaded) || forceShow) && <ConsentBanner forceShow={forceShow} bannerMode={bannerMode} />}
		</ConsentContext.Provider>
	);
}

export function useConsent() {
	return useContext(ConsentContext)
}
export default ConsentProvider;
